import { cn } from "../utils/cn";
import { formatCurrency, getInitials, hashStringToColor } from "../utils/formatters";
import { Button } from "./Button";
import { BadgePill } from "./BadgePill";
import { StarRating } from "./StarRating";
import { PriceBadge } from "./PriceBadge";
import { BatchSlotBar } from "./BatchSlotBar";
import { InclusionChip } from "./InclusionChip";
import type { BadgeTier } from "../types/common";

// ── Icons ─────────────────────────────────────────────────────────────────────

function CalendarIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" className="shrink-0">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"
      aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OperatorCardProps {
  operatorName: string;
  operatorSlug: string;
  logoUrl?: string;
  badgeTier: BadgeTier;
  currentPrice: number;
  priceVsAvg: number;
  avgRating: number;
  totalReviews: number;
  nextBatchDate: string;
  slotsRemaining: number;
  totalSlots: number;
  extraInclusions: string[];
  missingInclusions: string[];
  isSelected: boolean;
  isRecommended?: boolean;
  onSelect: () => void;
  onViewDetails: () => void;
  className?: string;
}

// ── Operator logo ─────────────────────────────────────────────────────────────

function OperatorLogo({ url, name }: { url?: string; name: string }) {
  const initials = getInitials(name);
  const bg       = hashStringToColor(name);

  return (
    <div className="relative w-10 h-10 rounded-full overflow-hidden shrink-0 ring-2 ring-white dark:ring-surface">
      {url ? (
        <img src={url} alt={name} className="w-full h-full object-cover" />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center text-white text-sm font-bold"
          style={{ backgroundColor: bg }}
        >
          {initials}
        </div>
      )}
    </div>
  );
}

// ── Slot urgency indicator ────────────────────────────────────────────────────

function UrgencyDot({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 dark:text-red-400">
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
      </span>
      Only {count} left
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const MAX_VISIBLE_INCLUSIONS = 3;

export function OperatorCard({
  operatorName,
  logoUrl,
  badgeTier,
  currentPrice,
  priceVsAvg,
  avgRating,
  totalReviews,
  nextBatchDate,
  slotsRemaining,
  totalSlots,
  extraInclusions,
  missingInclusions,
  isSelected,
  isRecommended,
  onSelect,
  onViewDetails,
  className,
}: OperatorCardProps) {
  // Merge inclusions (extra first, then missing), cap display at 3
  const allInclusions = [
    ...extraInclusions.map((l) => ({ label: l, type: "included" as const })),
    ...missingInclusions.map((l) => ({ label: l, type: "missing" as const })),
  ];
  const visible       = allInclusions.slice(0, MAX_VISIBLE_INCLUSIONS);
  const overflowCount = allInclusions.length - visible.length;
  const hasInclusions = allInclusions.length > 0;
  const fullyBooked   = slotsRemaining === 0;

  return (
    <article
      className={cn(
        "flex flex-col gap-4 p-4 rounded-card transition-all duration-200",
        isSelected
          ? [
              "border-2 border-brand-purple dark:border-violet-500",
              "bg-violet-50/40 dark:bg-violet-500/12",
              "shadow-md",
            ]
          : [
              "border border-border-default",
              "bg-white dark:bg-surface-raised",
              "shadow-sm hover:shadow-md",
            ],
        className,
      )}
      aria-selected={isSelected}
    >
      {/* ── Header: logo + name + badge ─────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <OperatorLogo url={logoUrl} name={operatorName} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-content-primary leading-snug truncate">
              {operatorName}
            </p>
            <div className="mt-1">
              <BadgePill tier={badgeTier} size="sm" showLabel />
            </div>
          </div>
        </div>

        {/* Recommended pill */}
        {isRecommended && (
          <span className={cn(
            "shrink-0 inline-flex items-center gap-1 px-2 py-0.5",
            "text-[11px] font-semibold rounded-full border",
            "bg-teal-50 text-teal-700 border-teal-200",
            "dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-800/40",
          )}>
            <CheckCircleIcon />
            Recommended
          </span>
        )}
      </div>

      {/* ── Price section ───────────────────────────────────────── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline gap-1.5">
          <span
            className="text-[22px] font-bold leading-none text-brand-purple dark:text-violet-400"
            aria-label={`Price: ${formatCurrency(currentPrice)}`}
          >
            {formatCurrency(currentPrice)}
          </span>
          <span className="text-xs text-content-tertiary">/ person</span>
        </div>
        <PriceBadge priceVsAvg={priceVsAvg} size="sm" />
      </div>

      {/* ── Middle: rating | batch date + slot bar ─────────────── */}
      <div className="flex items-stretch gap-3">
        {/* Rating */}
        <div className="flex-1 flex items-center">
          <StarRating value={avgRating} count={totalReviews} size="sm" />
        </div>

        {/* Divider */}
        <div className="w-px self-stretch bg-border-default" />

        {/* Batch date + slot bar */}
        <div className="flex-1 flex flex-col gap-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1 text-[12px] text-content-secondary font-medium">
              <CalendarIcon />
              {nextBatchDate}
            </span>
            {slotsRemaining > 0 && slotsRemaining <= 3 && (
              <UrgencyDot count={slotsRemaining} />
            )}
          </div>
          <BatchSlotBar
            total={totalSlots}
            remaining={slotsRemaining}
            showLabel
          />
        </div>
      </div>

      {/* ── Inclusions comparison ───────────────────────────────── */}
      {hasInclusions && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">
            vs other operators:
          </p>
          <div className="flex flex-wrap gap-1.5 items-center">
            {visible.map((item, i) => (
              <InclusionChip
                key={i}
                label={item.label}
                type={item.type}
                size="sm"
              />
            ))}
            {overflowCount > 0 && (
              <span className="text-[11px] font-medium text-content-tertiary">
                +{overflowCount} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Actions ─────────────────────────────────────────────── */}
      <div className="flex gap-2 pt-1 border-t border-border-default">
        <Button
          variant={isSelected ? "primary" : "secondary"}
          size="sm"
          onClick={onSelect}
          disabled={fullyBooked}
          className="flex-1"
          aria-pressed={isSelected}
        >
          {fullyBooked
            ? "Fully booked"
            : isSelected
              ? "✓ Selected"
              : "Select this operator"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onViewDetails}
        >
          View details
        </Button>
      </div>
    </article>
  );
}

OperatorCard.displayName = "OperatorCard";
